import React from 'react';
import { ChevronLeft, ChevronRight, Check, ArrowRight, Calendar } from 'lucide-react';
import { BookingConfig, FormSection } from '../../types/booking';

interface WizardLayoutProps {
  sections: FormSection[];
  currentSection: number;
  onSectionChange: (section: number) => void;
  config: BookingConfig;
  children: React.ReactNode;
  onNext: () => boolean;
  onPrevious: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export const WizardLayout: React.FC<WizardLayoutProps> = ({
  sections,
  currentSection,
  onSectionChange,
  config,
  children,
  onNext,
  onPrevious,
  onSubmit,
  isSubmitting
}) => {
  const { theme, darkMode } = config;
  const enabledSections = sections.filter(section => section.enabled).sort((a, b) => a.order - b.order);
  const totalSteps = enabledSections.length;
  const isLastStep = currentSection === totalSteps - 1;
  const isFirstStep = currentSection === 0;
  const currentSectionData = enabledSections[currentSection];

  const handleNext = () => {
    if (isLastStep) {
      onSubmit();
    } else {
      const canProceed = onNext();
      if (canProceed) {
        onSectionChange(currentSection + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      onPrevious();
      onSectionChange(currentSection - 1);
    }
  };

  // Top Progress Bar Component
  const ProgressBar = () => {
    if (!config.features.progressIndicator) return null;

    return (
      <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl shadow-lg p-6 mb-4">
        <div className="flex items-center justify-between">
            {enabledSections.map((section, index) => (
              <React.Fragment key={section.id}>
              <div className="flex flex-col items-center space-y-2">
                <div
                  className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all duration-300 ${
                    index < currentSection
                      ? 'bg-green-500 text-white'
                      : index === currentSection
                      ? 'bg-purple-500 text-white'
                      : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {index < currentSection ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    index + 1
                  )}
                </div>
                <span
                  className={`text-sm font-medium text-center max-w-[100px] ${
                    index <= currentSection
                      ? 'text-gray-900 dark:text-white'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {section.name}
                </span>
                </div>
                {index < enabledSections.length - 1 && (
                  <div 
                  className={`flex-1 h-0.5 mx-4 transition-all duration-300 ${
                    index < currentSection ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
      </div>
    );
  };

  // Hero section gets special treatment
  if (currentSectionData?.id === 'hero') {
    return (
      <div className="bg-transparent py-8">
        <div className="max-w-5xl mx-auto px-4">
          <ProgressBar />
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
            {/* Left side - Hero Visual */}
            <div className="lg:col-span-2">
              <div className="w-full aspect-square bg-gradient-to-r from-purple-400/80 via-purple-500/80 to-purple-600/80 backdrop-blur-md border border-white/20 rounded-3xl overflow-hidden relative flex items-center justify-center">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 via-transparent to-transparent"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-purple-700/20 to-transparent rounded-b-3xl"></div>
                
                {/* Add some visual content to reduce empty space */}
                <div className="relative z-10 text-center">
                  <div className="w-20 h-20 mx-auto mb-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center">
                    <Calendar className="w-10 h-10 text-white" />
                  </div>
                  <p className="text-white/90 font-medium text-lg">{config.localization.translations['booking.planYourCall'] || 'Plan je gesprek'}</p>
                </div>
              </div>
            </div>

            {/* Right side - Content */}
            <div className="lg:col-span-3 space-y-4">
              <div className="space-y-3">
                <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white">
                  {config.title}
                </h1>
                <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                  {config.description}
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-300">
                  <div className="w-5 h-5 bg-green-500/80 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                  <span>{config.localization.translations['booking.freeConsultation'] || 'Gratis kennismakingsgesprek'}</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-300">
                  <div className="w-5 h-5 bg-green-500/80 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                  <span>{config.localization.translations['booking.personalizedAdvice'] || 'Persoonlijk advies op maat'}</span>
                </div>
                <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-300">
                  <div className="w-5 h-5 bg-green-500/80 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                  <span>{config.localization.translations['booking.onlineOrOnsite'] || 'Online of op locatie'}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleNext}
                className="bg-gradient-to-r from-cloud-blue/90 to-cloud-lavender/90 hover:from-cloud-blue hover:to-cloud-lavender backdrop-blur-md border border-white/20 text-white px-8 py-4 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 gradient-button flex items-center space-x-3"
              >
                <span>{config.localization.translations['button.bookNow'] || 'Nu boeken'}</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-transparent">
      <div className="max-w-4xl mx-auto px-4 py-2">
        <ProgressBar />
        <div className="min-h-[600px]">
          {/* Main Content - Full Width */}
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl shadow-lg p-6 h-full">
            {/* Section Header */}
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {currentSectionData?.title}
              </h1>
          {currentSectionData?.description && (
                <p className="text-gray-600 dark:text-gray-300">
              {currentSectionData.description}
            </p>
          )}
        </div>

      {/* Content */}
            <div className="space-y-4">
        {children}
      </div>

      {/* Navigation */}
            <div className="flex justify-between items-center mt-6 pt-4 border-t border-white/20">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {(config.localization.translations['booking.stepOf'] || 'Stap {current} van {total}')
                  .replace('{current}', String(currentSection + 1))
                  .replace('{total}', String(totalSteps))
                }
              </div>
              
              <div className="flex space-x-4">
                {!isFirstStep && (
        <button
          type="button"
          onClick={handlePrevious}
                    className="bg-white/20 backdrop-blur-sm border border-white/30 text-gray-700 dark:text-white px-6 py-3 text-lg font-semibold rounded-full transition-all duration-300 flex items-center space-x-2 hover:bg-white/30"
        >
          <ChevronLeft className="w-4 h-4" />
                    <span>{config.localization.translations['button.previous'] || 'Terug'}</span>
        </button>
                )}

        <button
          type="button"
          onClick={handleNext}
          disabled={isSubmitting}
                  className="bg-gradient-to-r from-cloud-blue to-cloud-lavender hover:from-cloud-blue/90 hover:to-cloud-lavender/90 text-white px-8 py-3 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 gradient-button flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>
                    {isLastStep 
                      ? (config.localization.translations['button.submit'] || 'Nu boeken')
                      : (config.localization.translations['button.next'] || 'Volgende')
                    }
                  </span>
              <ChevronRight className="w-4 h-4" />
        </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};